/**
 * Home's atlas slot — figure C5's "🗺 My inventions" card (Story #5648).
 *
 * screen-C5-accept-invitation.html, right column, above "Suggested next steps":
 * a heading, a counted sub-line, a 280×150 map of the inventor's own inventions
 * as coloured footprints, and "Open the atlas →".
 *
 * IT IS THE ATLAS, SMALLER — NOT A SECOND DRAWING OF THE SAME THING.
 * Every shape here comes from the same payload and the same exported geometry
 * MyInventionsAtlas uses:
 *
 *   getPortfolioMap(null, 'own')  →  the SAME react-query cache row
 *                                    (['portfolio-map', 'own']) the atlas
 *                                    reads, so opening the atlas from here
 *                                    costs no second request and the two can
 *                                    never disagree about where a shape is
 *   computeLayoutBounds            →  the run's own extent
 *   makeAtlasProjector             →  the SAME uniform fit-to-bounds, given
 *                                    SLOT_VIEWPORT instead of VIEWPORT
 *   computeGroupFootprints         →  the SAME footprint ellipses
 *   computeCentroid2D              →  the SAME invention centres
 *   projectTagColor                →  the SAME roster swatch colour, keyed on
 *                                    the same plotted-id order
 *
 * Nothing about the geometry is re-derived, so a change to the atlas's shapes
 * moves this card with it.
 *
 * WHAT IS DELIBERATELY LEFT OUT, and why it is not a partial atlas:
 * names, leader lines, the H/B/F shape markers, the overlap patches and the
 * "2-way · N" chip. At 280×150 there is no room to place a name honestly — and
 * the placer's whole job is refusing to draw a label it cannot connect — so
 * drawing any of them small would mean drawing them wrong. The card says how
 * many of each there are in its sub-line and sends the reader to the full atlas
 * for the rest, which is what the figure does too: its own slot draws six bare
 * ellipses and one link.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Map as MapIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  getPortfolioMap,
  getCachedPortfolioOverlap,
  listPortfolioWatches,
} from '../../services/api'
import {
  computeLayoutBounds,
  makeAtlasProjector,
  groupPortfolioLayoutByProject,
  computeCentroid2D,
  computeGroupFootprints,
  projectTagColor,
} from '../../lib/whitespace'
import { starPoints } from './atlasGeometry'
import {
  SLOT_VIEWPORT,
  atlasSlotCounts,
  atlasSlotSubLine,
  ownedInventions,
  slotPlottedIds,
} from './atlasSlotView'
import type { Project } from '../../types'

const OWN = 'own' as const

/** The star that marks an invention's centre, at the slot's scale. The atlas
 *  draws an 11/4.6 star on a 900×560 canvas; this is the same shape on a
 *  280×150 one, through atlasGeometry's own starPoints so the two can never
 *  drift into different stars. */
const STAR_OUTER = 5.5
const STAR_INNER = 2.3

export interface AtlasSlotProps {
  /** GET /projects, already held by Home — never fetched twice. */
  projects: Project[]
  /** Home's own "Documents analysed" number, so the sub-line and the KPI tile
   *  a few centimetres above it can never print different totals. */
  documentCount?: number | null
}

export default function AtlasSlot({ projects, documentCount }: AtlasSlotProps) {
  const navigate = useNavigate()

  const mapQuery = useQuery({
    queryKey: ['portfolio-map', OWN],
    queryFn: () => getPortfolioMap(null, OWN),
    retry: false,
  })
  const overlapQuery = useQuery({
    queryKey: ['portfolio-overlap', OWN, 'cached'],
    queryFn: () => getCachedPortfolioOverlap(OWN),
    retry: false,
  })
  const watchesQuery = useQuery({
    queryKey: ['portfolio-watches', OWN],
    queryFn: () => listPortfolioWatches(OWN),
    retry: false,
  })

  const owned = useMemo(() => ownedInventions(projects), [projects])
  const counts = useMemo(
    () =>
      atlasSlotCounts({
        projects,
        documentCount,
        overlap: overlapQuery.data ?? null,
        watches: watchesQuery.data ?? null,
      }),
    [projects, documentCount, overlapQuery.data, watchesQuery.data],
  )

  const layout = useMemo(() => mapQuery.data?.layout ?? {}, [mapQuery.data])
  const plottedIds = useMemo(() => slotPlottedIds(mapQuery.data, projects), [mapQuery.data, projects])
  const bounds = useMemo(() => computeLayoutBounds(layout), [layout])
  const projector = useMemo(() => makeAtlasProjector(bounds, SLOT_VIEWPORT), [bounds])
  const pointsByProject = useMemo(
    () => groupPortfolioLayoutByProject(layout, plottedIds),
    [layout, plottedIds],
  )
  const footprints = useMemo(() => computeGroupFootprints(layout, plottedIds), [layout, plottedIds])

  /**
   * FOOTPRINTS AND STARS ARE TWO DIFFERENT LISTS, and conflating them was a
   * real bug this file's tests caught: an invention has a footprint only when
   * computeFootprintEllipse can fit one to its points, but EVERY plotted
   * invention has a centre. On bob's own live capture that is 2 footprints and
   * 6 stars — deriving the stars from the footprints silently dropped four of
   * his inventions off the map while the sub-line still counted them. The atlas
   * iterates plottedIds for its stars and the footprint list for its ellipses,
   * so this does the same.
   */
  const drawnFootprints = useMemo(
    () =>
      footprints.map((f) => {
        const [cx, cy] = projector([f.cx, f.cy])
        const [ex] = projector([f.cx + f.rx, f.cy])
        const [, ey] = projector([f.cx, f.cy + f.ry])
        return {
          id: f.project_id,
          cx,
          cy,
          rx: Math.abs(ex - cx),
          ry: Math.abs(ey - cy),
          colour: projectTagColor(f.project_id, plottedIds),
        }
      }),
    [footprints, projector, plottedIds],
  )

  const drawnStars = useMemo(
    () =>
      plottedIds
        .map((id) => {
          const centre = computeCentroid2D(pointsByProject.get(id) ?? [])
          return centre
            ? { id, at: projector(centre), colour: projectTagColor(id, plottedIds) }
            : null
        })
        .filter((s): s is { id: string; at: [number, number]; colour: string } => s !== null),
    [plottedIds, pointsByProject, projector],
  )

  const nameById = useMemo(
    () => new Map(projects.map((p) => [p.project_id, p.name])),
    [projects],
  )

  /**
   * Nothing to draw = no footprints AND no stars. Either one alone is a real
   * picture: an invention can have a centre without a fittable footprint (that
   * is 4 of bob's 6), and the reverse is possible too, so the well is shown
   * whenever EITHER list has something in it.
   */
  const nothingToDraw = drawnFootprints.length === 0 && drawnStars.length === 0

  /**
   * And the two cases are told apart, because they are different facts and the
   * reader can act on only one of them. An account with no inventions is told
   * to make one; an account whose inventions exist but have no projected points
   * yet is told what it is waiting for, and is NOT told to go and create
   * something it already has.
   */
  const emptyMessage =
    owned.length === 0
      ? 'Nothing to map yet — your first invention will appear here.'
      : 'No overlap map yet — a document is drawn once its embedding completes.'

  return (
    <div className="light-card p-5" data-part="AtlasSlot">
      <div className="flex items-center gap-2 mb-1">
        <MapIcon size={16} className="text-iflow-500" />
        <h3 className="text-sm font-semibold text-gray-900">My inventions</h3>
      </div>
      <p className="text-xs text-gray-500" data-testid="atlas-slot-subline">
        {atlasSlotSubLine(counts)}
      </p>

      {nothingToDraw ? (
        /* THE EMPTY STATE IS ABOUT WHAT CAN BE DRAWN, NOT ABOUT HOW MANY
           INVENTIONS EXIST (round 9).

           It used to be gated on `owned.length === 0`, which the round-8 walk
           caught on the beta-owner persona: one invention, but
           GET /whitespace/portfolio_map?scope=own answers 400 "Portfolio has no
           documents to project", the query is retry:false, so `layout` stays {}
           — and the account fell straight past the empty state into an <svg>
           with no children. That rendered as a 387px blank well, which also
           stretched the "Recent inventions" card beside it.

           A map with no marks in it is not an empty state, it is a broken one.
           The condition is now "there is nothing to draw", which covers the
           400, a pending embedding, and a genuinely empty account alike, and
           the well is not rendered at all so nothing stretches. */
        <p className="mt-3 text-sm text-gray-600" data-testid="atlas-slot-empty">
          {emptyMessage}
        </p>
      ) : (
        <div className="mt-2 rounded-md bg-[#F8FBFD] p-1.5">
          <svg
            viewBox={`0 0 ${SLOT_VIEWPORT.width} ${SLOT_VIEWPORT.height}`}
            width="100%"
            className="block"
            role="img"
            aria-label={`Map of your ${counts.inventions} invention${counts.inventions === 1 ? '' : 's'}`}
            data-testid="atlas-slot-map"
          >
            {drawnFootprints.map((f) => (
              <ellipse
                key={`fp-${f.id}`}
                data-slot-footprint={f.id}
                cx={f.cx}
                cy={f.cy}
                rx={f.rx}
                ry={f.ry}
                fill={f.colour}
                fillOpacity={0.14}
                stroke={f.colour}
                strokeWidth={1}
              />
            ))}
            {drawnStars.map((s) => (
              <polygon
                key={`star-${s.id}`}
                data-slot-star={s.id}
                points={starPoints(s.at[0], s.at[1], STAR_OUTER, STAR_INNER)}
                fill={s.colour}
                stroke="#ffffff"
                strokeWidth={1}
              >
                <title>{nameById.get(s.id) ?? s.id}</title>
              </polygon>
            ))}
          </svg>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/projects?view=atlas')}
        className="mt-2 text-xs font-semibold text-iflow-600 hover:text-iflow-700"
      >
        Open the atlas →
      </button>
    </div>
  )
}
